import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Trash2, Sparkles, Wifi, Tv, Wind, Coffee, Bath, Car, Utensils, Dumbbell, Waves } from 'lucide-react';
import { toast } from 'sonner';

const iconOptions = [
  { value: 'wifi', label: 'WiFi', icon: Wifi },
  { value: 'tv', label: 'TV', icon: Tv },
  { value: 'ac', label: 'Air Conditioning', icon: Wind },
  { value: 'coffee', label: 'Coffee/Tea', icon: Coffee },
  { value: 'bath', label: 'Bathroom', icon: Bath },
  { value: 'parking', label: 'Parking', icon: Car },
  { value: 'restaurant', label: 'Restaurant', icon: Utensils },
  { value: 'gym', label: 'Gym', icon: Dumbbell },
  { value: 'pool', label: 'Pool', icon: Waves },
  { value: 'other', label: 'Other', icon: Sparkles },
];

const getIconComponent = (iconName) => {
  const option = iconOptions.find(o => o.value === iconName);
  return option ? option.icon : Sparkles;
};

export default function AmenityManagement() {
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [formData, setFormData] = useState({ name: '', icon: 'other', description: '' });

  useEffect(() => {
    loadAmenities();
  }, []);

  const loadAmenities = async () => {
    try {
      const response = await api.get('/amenities');
      setAmenities(response.data);
    } catch (error) {
      toast.error('Failed to load amenities');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAmenity) {
        await api.put(`/amenities/${editingAmenity.id}`, formData);
        toast.success('Amenity updated');
      } else {
        await api.post('/amenities', formData);
        toast.success('Amenity created');
      }
      setDialogOpen(false);
      setEditingAmenity(null);
      setFormData({ name: '', icon: 'other', description: '' });
      loadAmenities();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Operation failed'));
    }
  };

  const handleEdit = (amenity) => {
    setEditingAmenity(amenity);
    setFormData({
      name: amenity.name,
      icon: amenity.icon || 'other',
      description: amenity.description || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this amenity?')) return;
    try {
      await api.delete(`/amenities/${id}`);
      toast.success('Amenity deleted');
      loadAmenities();
    } catch (error) {
      toast.error('Failed to delete amenity');
    }
  };

  const handleToggleActive = async (amenity) => {
    try {
      await api.put(`/amenities/${amenity.id}`, { is_active: !amenity.is_active });
      toast.success(`Amenity ${amenity.is_active ? 'deactivated' : 'activated'}`);
      loadAmenities();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openNewDialog = () => {
    setEditingAmenity(null);
    setFormData({ name: '', icon: 'other', description: '' });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="amenities-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>Amenities</h1>
          <p className="text-gray-500 text-sm mt-1">Manage room amenities</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNewDialog} className="bg-gradient-purple hover:opacity-90 text-white btn-glow-purple" data-testid="add-amenity-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Amenity
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>{editingAmenity ? 'Edit Amenity' : 'Add New Amenity'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Amenity Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Free WiFi, Air Conditioning"
                  className="border-purple-200 focus:border-purple-400"
                  data-testid="amenity-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select value={formData.icon} onValueChange={(value) => setFormData({ ...formData, icon: value })}>
                  <SelectTrigger className="border-purple-200">
                    <SelectValue placeholder="Select an icon" />
                  </SelectTrigger>
                  <SelectContent>
                    {iconOptions.map((option) => {
                      const Icon = option.icon;
                      return (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            <span>{option.label}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                  className="border-purple-200 focus:border-purple-400"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-gradient-purple hover:opacity-90 text-white" data-testid="save-amenity-btn">
                  {editingAmenity ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-purple-100 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-gray-800">
            <Sparkles className="h-5 w-5 text-purple-500" />
            All Amenities
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : amenities.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No amenities found. Add your first amenity!</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-purple-100">
                  <TableHead>Icon</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {amenities.map((amenity) => {
                  const Icon = getIconComponent(amenity.icon);
                  return (
                    <TableRow key={amenity.id} className="border-purple-100 table-row-hover">
                      <TableCell>
                        <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
                          <Icon className="h-4 w-4 text-purple-600" />
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-gray-800">{amenity.name}</TableCell>
                      <TableCell className="text-gray-500">{amenity.description || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={amenity.is_active}
                            onCheckedChange={() => handleToggleActive(amenity)}
                            data-testid={`toggle-${amenity.id}`}
                          />
                          <span className={amenity.is_active ? 'text-emerald-600 text-sm' : 'text-gray-400 text-sm'}>
                            {amenity.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(amenity)} className="text-gray-500 hover:text-purple-600">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(amenity.id)} className="text-gray-500 hover:text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
